import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const OPEN = ["pending_review", "needs_information", "submitted"];
const FILTERS = [
  { id: "open", label: "Under review", test: (a) => OPEN.includes(a.status) },
  { id: "approved", label: "Approved", test: (a) => a.status === "approved" && !a.pilot?.expired },
  { id: "expiring", label: "Expiring soon", test: (a) => a.status === "approved" && !!a.pilot?.expiring_soon && !a.pilot?.expired },
  { id: "rejected", label: "Rejected", test: (a) => a.status === "rejected" },
  { id: "expired", label: "Expired", test: (a) => a.status === "approved" && a.pilot?.expired },
  { id: "all", label: "All", test: () => true },
];
const STATUS_TAG = { pending_review: "bg-butter", needs_information: "bg-sky", approved: "bg-mint", rejected: "bg-peach" };
const STATUS_LABEL = { pending_review: "Under review", needs_information: "Info requested", approved: "Approved", rejected: "Rejected", submitted: "Submitted" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const Status = ({ a }) => (
  <>
    <span className={`nb-tag ${a.pilot?.expired ? "bg-gray-200" : STATUS_TAG[a.status] || "bg-gray-100"}`} data-testid={`edu-status-${a.application_id}`}>
      {a.pilot?.expired ? "Pilot expired" : STATUS_LABEL[a.status] || a.status}
    </span>
    {a.pilot?.expiring_soon && !a.pilot?.expired && <span className="nb-tag bg-amber-200" data-testid={`edu-expiring-${a.application_id}`}>Expiring soon</span>}
  </>
);

function Summary({ s }) {
  const cards = [["Under review", s.pending, "bg-butter", "pending"], ["Approved", s.approved, "bg-mint", "approved"],
    ["Rejected", s.rejected, "bg-peach", "rejected"], ["Expiring soon", s.expiring_soon_pilots, "bg-amber-100", "expiring"],
    ["Expired pilots", s.expired_pilots, "bg-gray-100", "expired"], ["Total schools", s.total_schools, "bg-lavender", "schools"]];
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6">
      {cards.map(([l, v, bg, id]) => (
        <div key={id} className={`nb-card ${bg} p-4`} data-testid={`edu-sum-${id}`}>
          <div className="font-cabinet font-extrabold text-3xl">{v ?? 0}</div>
          <div className="text-xs font-bold">{l}</div>
        </div>
      ))}
    </div>
  );
}

function Row({ a, onOpen }) {
  return (
    <button onClick={() => onOpen(a.application_id)} className="nb-card p-4 w-full text-start hover:-translate-y-0.5 transition-transform" data-testid={`edu-app-${a.application_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-cabinet font-bold text-lg">{a.institution_name}</div>
        <div className="flex gap-1"><VTag s={a.verification_status} id={a.application_id} /><Status a={a} /></div>
      </div>
      <div className="text-sm text-gray-600 mt-1 break-words">{a.applicant_name || "—"} · {a.applicant_email || "—"}</div>
      <div className="text-xs text-gray-500 mt-1">
        {a.institution_type} · {a.country || "no country"} · role: {a.requested_role} · submitted {fmt(a.submitted_at)}
        {a.pilot ? ` · ${a.pilot.education_program_type || "pilot"} ${a.pilot.expired ? "ended" : "until"} ${fmt(a.pilot.education_access_end)} · school ${a.pilot.status || "—"} · Education ${a.pilot.education_status || "—"}` : ""}
      </div>
    </button>
  );
}

function RejectForm({ busy, onReject }) {
  const [internal, setInternal] = useState("");
  const [msg, setMsg] = useState("");
  return (
    <div className="nb-card p-4 mt-3 space-y-2 bg-peach/30" data-testid="edu-reject-form">
      <label className="text-xs font-bold">Internal reason (staff only, never shown to the applicant)</label>
      <textarea className="nb-input text-sm" rows="2" value={internal} onChange={(e) => setInternal(e.target.value)} data-testid="edu-reject-internal" />
      <label className="text-xs font-bold">Message to applicant (optional)</label>
      <textarea className="nb-input text-sm" rows="2" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="If empty they see: “Your school application was not approved at this time.”" data-testid="edu-reject-message" />
      <button disabled={busy} onClick={() => onReject(internal, msg)} className="nb-btn-outline py-2 px-4 text-sm text-coral border-coral" data-testid="edu-reject-confirm">Confirm rejection</button>
    </div>
  );
}

const ROLE_LABEL = { teacher: "Teacher", school_admin: "School admin", principal: "Principal", director: "Director", coordinator: "Coordinator", other: "Other" };
const reqLabel = (r) => ROLE_LABEL[r] || (r ? r.replace("_", " ") : "—");
const V_LABEL = { unverified: "Unverified", needs_verification: "Needs verification", verified: "Verified" };
const V_TAG = { unverified: "bg-gray-100", needs_verification: "bg-butter", verified: "bg-mint" };
const EMAIL_STATUS = { none: "No school email given", public_provider: "Personal email provider (e.g. Gmail) — not institutional",
  institutional_unmatched: "Institutional-looking domain, not linked to a verified school", matches_verified_school: "Domain matches a verified school" };
const PROOFS = { institutional_email: "Institutional email", staff_page: "Official staff-page link", school_admin_confirmation: "Confirmation from a school admin", other: "Other affiliation evidence" };

const VTag = ({ s, id }) => <span className={`nb-tag ${V_TAG[s] || "bg-gray-100"}`} data-testid={id ? `edu-v-status-${id}` : "edu-v-status"}>{V_LABEL[s] || s}</span>;
const SafeLink = ({ url }) => (/^https?:\/\//i.test(url || "") ? <a href={url} target="_blank" rel="noopener noreferrer nofollow" className="underline break-all">{url}</a> : <span className="break-all">{url || "—"}</span>);

function VEvent({ e }) {
  const when = new Date(e.at).toLocaleString();
  if (e.type === "requested") return <li className="text-xs">{when} · <b>Verification requested</b>{e.proofs?.length ? `: ${e.proofs.map((p) => PROOFS[p]).join(", ")}` : ""}{e.message ? ` — “${e.message}”` : ""}</li>;
  if (e.type === "response") return (
    <li className="text-xs">{when} · <b>Applicant responded</b>
      {e.institutional_email && <div className="ms-3">Institutional email (evidence only): <span className="break-all">{e.institutional_email}</span></div>}
      {e.staff_page_url && <div className="ms-3">Staff page: <SafeLink url={e.staff_page_url} /></div>}
      {e.note && <div className="ms-3">Note: {e.note}</div>}
    </li>);
  return <li className="text-xs">{when} · <b>Marked verified</b>{e.evidence_considered ? ` — evidence: ${e.evidence_considered}` : ""}{e.internal_note ? ` · internal: ${e.internal_note}` : ""}</li>;
}

function Verification({ d, busy, act }) {
  const [mode, setMode] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [msg, setMsg] = useState("");
  const [evidence, setEvidence] = useState("");
  const [note, setNote] = useState("");
  const ev = d.evidence || {};
  const open = OPEN.includes(d.status);
  const toggle = (p) => setProofs((x) => (x.includes(p) ? x.filter((y) => y !== p) : [...x, p]));
  return (
    <div className="nb-card p-3" data-testid="edu-verification">
      <div className="flex items-center justify-between gap-2"><div className="font-bold text-sm">Educator verification</div><VTag s={d.verification_status} /></div>
      <div className="space-y-1 mt-2">
        <KV k="Requested role" v={reqLabel(d.requested_role)} id="edu-v-role" />
        <KV k="Email / domain" v={ev.email_domain ? `${ev.email_domain} — ${EMAIL_STATUS[ev.email_status]}${ev.email_matched_school ? ` (${ev.email_matched_school})` : ""}` : EMAIL_STATUS.none} id="edu-v-email" />
        <div className="flex gap-2 text-sm"><span className="text-gray-500 w-36 shrink-0">School website</span><span data-testid="edu-v-website"><SafeLink url={d.institution_website} />{ev.website_matches_email ? " · matches email domain" : ""}</span></div>
        <KV k="Existing school" v={d.pilot?.name || d.likely_existing_org?.name || "No match"} id="edu-v-school" />
        <KV k="Invited by" v={ev.invited_by ? `${ev.invited_by.name || ""} ${ev.invited_by.email || ""}` : "No invitation"} id="edu-v-invited" />
        <KV k="Attestation" v={ev.attestation?.accepted ? `Confirmed ${new Date(ev.attestation.at).toLocaleDateString()}` : "Not captured (submitted before attestation existed)"} id="edu-v-attestation" />
      </div>
      {(d.verification_history || []).length > 0 && <ol className="mt-2 space-y-1" data-testid="edu-v-history">{d.verification_history.map((e) => <VEvent key={e.id} e={e} />)}</ol>}
      {open && d.verification_status !== "verified" && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button disabled={busy} onClick={() => setMode(mode === "req" ? null : "req")} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="edu-v-request">Request verification</button>
          <button disabled={busy} onClick={() => setMode(mode === "ver" ? null : "ver")} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="edu-v-mark">Mark verified</button>
        </div>
      )}
      {mode === "req" && (
        <div className="mt-2 space-y-2" data-testid="edu-v-request-form">
          {Object.entries(PROOFS).map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={proofs.includes(k)} onChange={() => toggle(k)} data-testid={`edu-v-proof-${k}`} /> {l}</label>
          ))}
          <textarea className="nb-input text-sm" rows="2" placeholder="Message the applicant will see (optional)" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="edu-v-request-message" />
          <button disabled={busy || (!proofs.length && !msg.trim())} onClick={() => { act("request-verification", { proofs, message: msg }); setMode(null); }} className="nb-btn-primary py-1.5 px-3 text-sm" data-testid="edu-v-request-send">Send request</button>
        </div>
      )}
      {mode === "ver" && (
        <div className="mt-2 space-y-2" data-testid="edu-v-mark-form">
          <textarea className="nb-input text-sm" rows="2" placeholder="Evidence considered (staff only)" value={evidence} onChange={(e) => setEvidence(e.target.value)} data-testid="edu-v-evidence" />
          <textarea className="nb-input text-sm" rows="2" placeholder="Internal note (staff only, optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="edu-v-note" />
          <button disabled={busy} onClick={() => { act("verify", { evidence_considered: evidence, internal_note: note }); setMode(null); }} className="nb-btn-primary py-1.5 px-3 text-sm" data-testid="edu-v-mark-confirm">Confirm verified</button>
        </div>
      )}
    </div>
  );
}

function Actions({ d, busy, act }) {
  const [program, setProgram] = useState("standard_pilot");
  const [role, setRole] = useState(d.default_role || "teacher");
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  if (!OPEN.includes(d.status)) return null;
  const differs = role !== d.default_role;
  const verified = d.verification_status === "verified";
  const approve = () => act("approve", { program_type: program, role });
  return (
    <div className="mt-4" data-testid="edu-actions">
      <div className="flex flex-wrap gap-2 items-center">
        <select value={program} onChange={(e) => setProgram(e.target.value)} className="nb-input text-sm py-1.5 w-auto" data-testid="edu-program">
          <option value="standard_pilot">Standard pilot · 90 days</option>
          <option value="extended_pilot">Extended pilot · 180 days</option>
          <option value="founding_school">Founding school · 365 days</option>
        </select>
        <label className="text-xs font-bold">Approve as
          <select value={role} onChange={(e) => { setRole(e.target.value); setConfirming(false); }} className="nb-input text-sm py-1.5 w-auto ms-2" data-testid="edu-role">
            <option value="teacher">Teacher</option>
            <option value="school_admin">School admin</option>
          </select>
        </label>
        <button disabled={busy || !verified} title={verified ? "" : "Mark the applicant verified first"} onClick={() => (differs ? setConfirming(true) : approve())} className="nb-btn-primary py-2 px-4 text-sm disabled:opacity-50" data-testid="edu-approve">Approve school</button>
        <button disabled={busy} onClick={() => setRejecting((r) => !r)} className="nb-btn-outline py-2 px-4 text-sm text-coral border-coral" data-testid="edu-reject">Reject</button>
      </div>
      <p className="text-xs text-gray-500 mt-2" data-testid="edu-role-summary">Requested: <b>{reqLabel(d.requested_role)}</b> · Approve as: <b>{ROLE_LABEL[role]}</b></p>
      {!verified && <p className="text-xs font-bold text-coral mt-1" data-testid="edu-approve-blocked">Approval is available after the applicant is marked Verified.</p>}
      {confirming && differs && (
        <div className="nb-card p-3 mt-2 bg-butter/50 text-sm" data-testid="edu-role-confirm">
          <div>Requested: <b>{reqLabel(d.requested_role)}</b></div>
          <div>Approve as: <b>{ROLE_LABEL[role]}</b></div>
          <div className="flex gap-2 mt-2">
            <button disabled={busy} onClick={approve} className="nb-btn-primary py-1.5 px-3 text-sm" data-testid="edu-role-confirm-approve">Confirm approval</button>
            <button onClick={() => setConfirming(false)} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="edu-role-confirm-cancel">Cancel</button>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2">Not sure yet? Just close this panel — the application stays under review.</p>
      {rejecting && <RejectForm busy={busy} onReject={(internal_reason, applicant_message) => act("reject", { internal_reason, applicant_message })} />}
    </div>
  );
}

const KV = ({ k, v, id, mono }) => (
  <div className="flex gap-2 text-sm"><span className="text-gray-500 w-36 shrink-0">{k}</span><span className={`font-medium ${mono ? "break-all" : "break-words min-w-0"}`} data-testid={id}>{v ?? "—"}</span></div>
);

function Detail({ d, busy, act }) {
  const org = d.pilot || d.likely_existing_org;
  return (
    <div className="space-y-5 mt-4" data-testid="edu-detail">
      <div className="space-y-1">
        <KV k="Application ID" v={d.application_id} id="edu-d-id" mono />
        <KV k="Status" v={STATUS_LABEL[d.status] || d.status} id="edu-d-status" />
        <KV k="Applicant" v={`${d.applicant_name || "—"} · ${d.applicant_email || "—"}`} mono />
        <KV k="User ID" v={d.applicant_user_id} mono />
        <KV k="Institution" v={`${d.institution_name} (${d.institution_type})`} />
        <KV k="Country" v={d.country} />
        <KV k="Website" v={d.institution_website} mono />
        <KV k="School email" v={d.school_email} mono />
        <KV k="Requested role" v={reqLabel(d.requested_role)} id="edu-d-requested-role" />
        {d.approved_role && <KV k="Approved as" v={ROLE_LABEL[d.approved_role] || d.approved_role} id="edu-d-approved-role" />}
        <KV k="Approx. learners" v={d.approx_student_count} />
        <KV k="Verification" v={d.verification_method} />
        <KV k="Submitted" v={d.submitted_at && new Date(d.submitted_at).toLocaleString()} />
        {d.notes && <KV k="Applicant note" v={d.notes} />}
        {d.review_notes && <KV k="Review note" v={d.review_notes} />}
        {d.internal_reason && <KV k="Internal reason" v={d.internal_reason} />}
        {d.applicant_message && <KV k="Message to applicant" v={d.applicant_message} />}
      </div>
      <div className="nb-card p-3" data-testid="edu-d-org">
        <div className="font-bold text-sm">{d.pilot ? "School organization" : d.likely_existing_org ? "Likely existing organization (approval would reuse it)" : "No organization yet — approval creates one"}</div>
        {org && <div className="text-xs text-gray-600 mt-1">{org.name} · {org.org_id} · {org.verification_status} · Education {org.education_status}{org.expired ? " (expired)" : ""} · {fmt(org.education_access_start)} → {fmt(org.education_access_end)}</div>}
        {d.pilot && <div className="text-xs text-gray-600 mt-1">Members: {Object.entries(d.members_by_role || {}).map(([r, n]) => `${n} ${r}`).join(", ") || "none"} · classes: {d.class_count}</div>}
      </div>
      {d.duplicates?.length > 0 && (
        <div className="nb-card p-3 bg-butter/50" data-testid="edu-d-duplicates">
          <div className="font-bold text-sm flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Possible duplicates / related</div>
          {d.duplicates.map((x) => <div key={x.application_id} className="text-xs mt-1">{x.institution_name} · {STATUS_LABEL[x.status] || x.status} · {fmt(x.submitted_at)} · {x.application_id}</div>)}
        </div>
      )}
      <div data-testid="edu-d-history">
        <div className="font-bold text-sm">Status history</div>
        <ol className="mt-1 space-y-1">
          {(d.history || []).map((h) => <li key={h.id} className="text-xs text-gray-600">{new Date(h.created_at).toLocaleString()} · <b>{h.action.replaceAll("_", " ")}</b>{h.meta?.approved_role ? ` · requested ${reqLabel(h.meta.requested_role)}, approved as ${ROLE_LABEL[h.meta.approved_role]}` : ""}</li>)}
          {(!d.history || d.history.length === 0) && <li className="text-xs text-gray-500">No events recorded.</li>}
        </ol>
      </div>
      <Verification key={`v-${d.application_id}`} d={d} busy={busy} act={act} />
      <Actions key={d.application_id} d={d} busy={busy} act={act} />
    </div>
  );
}

export default function EducationAdmin() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [sum, setSum] = useState({});
  const [filter, setFilter] = useState("open");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/applications").then((r) => setApps(r.data.applications || [])).catch(() => {});
    api.get("/admin/education/summary").then((r) => setSum(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.is_admin) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDetail(null);
    if (openId) api.get(`/admin/applications/${openId}`).then((r) => setDetail(r.data)).catch(() => toast.error("Could not load application."));
  }, [openId]);

  const act = async (action, body) => {
    setBusy(true);
    try {
      await api.post(`/admin/applications/${openId}/${action}`, body);
      toast.success({ approve: "School approved — pilot started", reject: "Application rejected", "request-verification": "Verification requested", verify: "Marked verified" }[action]);
      load();
      window.dispatchEvent(new Event("edu-pending-changed"));
      const r = await api.get(`/admin/applications/${openId}`);
      setDetail(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  if (!user?.is_admin) {
    return (
      <AppLayout>
        <Seo title="Education Admin · FluentLoop" noindex />
        <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="edu-admin-denied">
          <ShieldCheck className="w-12 h-12 mx-auto" />
          <h1 className="font-cabinet font-extrabold text-2xl mt-4">Admin access required</h1>
        </div>
      </AppLayout>
    );
  }
  const f = FILTERS.find((x) => x.id === filter);
  const shown = apps.filter(f.test);
  return (
    <AppLayout>
      <Seo title="Education Admin · FluentLoop" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="edu-admin-page">
        <Link to="/admin" className="text-sm text-gray-500 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Admin</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mt-2 flex items-center gap-2"><GraduationCap className="w-7 h-7" /> Education Admin</h1>
        <p className="text-sm text-gray-600">School applications and pilots. Approving starts the school's pilot; nothing changes for personal subscriptions.</p>
        <Summary s={sum} />
        <div className="flex flex-wrap gap-2 mt-6" role="tablist">
          {FILTERS.map((x) => (
            <button key={x.id} role="tab" aria-selected={filter === x.id} onClick={() => setFilter(x.id)} data-testid={`edu-filter-${x.id}`}
              className={`px-3 py-1.5 rounded-full border-2 border-ink text-sm font-bold ${filter === x.id ? "bg-ink text-white" : "bg-white"}`}>
              {x.label} · {apps.filter(x.test).length}
            </button>
          ))}
        </div>
        <div className="space-y-3 mt-4" data-testid="edu-app-list">
          {shown.length === 0 && <p className="text-sm text-gray-500">No applications here.</p>}
          {shown.map((a) => <Row key={a.application_id} a={a} onOpen={setOpenId} />)}
        </div>
      </div>
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="edu-detail-sheet">
          <SheetHeader><SheetTitle>{detail?.institution_name || "Application"}</SheetTitle></SheetHeader>
          {detail ? <Detail d={detail} busy={busy} act={act} /> : <div className="nb-card h-40 mt-4 animate-pulse" />}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}
